/**
 * Superadmin "sign in as user" state.
 * The impersonation credential is kept for this browser tab so a refresh
 * stays signed in as the user. It is never written to localStorage, cookies,
 * or logs. The superadmin Token captured before start stays in place until
 * exit succeeds or the session expires.
 */

const TAB_SESSION_KEY = 'deviare.impersonation';

const STORAGE_KEYS = ['ssoToken', 'enterprise_token', 'token', 'role'];
export const IMPERSONATION_BLOCKED_MESSAGE =
  'This action is blocked while signed in as a user. Return to the superadmin account first.';
export const DEFAULT_IMPERSONATION_SECONDS = 30 * 60;

let impersonationToken = null;
let savedStorage = null;
let session = inactiveSnapshot();
const listeners = new Set();
let lastNotice = { message: '', at: 0 };
let skipDashboardRedirect = false;

function inactiveSnapshot() {
  return {
    active: false,
    targetName: '',
    targetUsername: '',
    expiresInSeconds: 0,
    expiresAt: 0,
    keycloakSwitched: false,
    externalPlatformsSwitched: false,
  };
}

function readStorage() {
  const saved = {};
  STORAGE_KEYS.forEach((key) => {
    saved[key] = localStorage.getItem(key);
  });
  return saved;
}

function restoreSuperadminStorage() {
  if (!savedStorage) return;
  STORAGE_KEYS.forEach((key) => {
    const value = savedStorage[key];
    if (value == null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  });
}

function emit() {
  const snapshot = getImpersonationSnapshot();
  listeners.forEach((listener) => listener(snapshot));
}

export function getImpersonationSnapshot() {
  return { ...session };
}

export function subscribeImpersonation(listener) {
  listeners.add(listener);
  listener(getImpersonationSnapshot());
  return () => listeners.delete(listener);
}

export function isImpersonating() {
  return Boolean(impersonationToken);
}

export function getImpersonationTargetUsername() {
  return session.targetUsername || '';
}

export function shouldNotify(message) {
  const now = Date.now();
  if (message && message === lastNotice.message && now - lastNotice.at < 1500) {
    return false;
  }
  lastNotice = { message, at: now };
  return true;
}

export function impersonationWriteBlock() {
  if (!isImpersonating()) return null;
  return { status: false, message: IMPERSONATION_BLOCKED_MESSAGE };
}

export function rememberSuperadminToken() {
  savedStorage = readStorage();
  return savedStorage.ssoToken || '';
}

export function getAuthorizationValue() {
  if (impersonationToken) {
    return `Impersonation ${impersonationToken}`;
  }
  const token = localStorage.getItem('ssoToken');
  return token ? `Token ${token}` : '';
}

export function targetDisplayName(target) {
  if (!target || typeof target !== 'object') return 'this user';
  const name = [target.firstName, target.lastName].filter(Boolean).join(' ').trim();
  return name || target.userName || target.email || 'this user';
}

function writeTabSession() {
  if (!impersonationToken || !session.active || session.expiresAt <= Date.now()) {
    sessionStorage.removeItem(TAB_SESSION_KEY);
    return;
  }
  try {
    sessionStorage.setItem(TAB_SESSION_KEY, JSON.stringify({
      token: impersonationToken,
      savedStorage,
      session,
    }));
  } catch (error) {
    sessionStorage.removeItem(TAB_SESSION_KEY);
  }
}

function clearTabSession() {
  sessionStorage.removeItem(TAB_SESSION_KEY);
}

function restorePersistedSession() {
  let raw = null;
  try {
    raw = sessionStorage.getItem(TAB_SESSION_KEY);
  } catch (error) {
    return;
  }
  if (!raw) return;
  try {
    const parsed = JSON.parse(raw);
    const expiresAt = Number(parsed?.session?.expiresAt);
    if (!parsed?.token || !parsed?.session?.active || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      clearTabSession();
      return;
    }
    impersonationToken = parsed.token;
    savedStorage = parsed.savedStorage || null;
    session = {
      ...inactiveSnapshot(),
      ...parsed.session,
      active: true,
      expiresAt,
      expiresInSeconds: Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000)),
    };
  } catch (error) {
    clearTabSession();
  }
}

function adoptSession(token, data) {
  const secondsValue = Number(data?.expires_in_seconds);
  const expiresInSeconds = Number.isFinite(secondsValue) && secondsValue > 0
    ? secondsValue
    : DEFAULT_IMPERSONATION_SECONDS;
  impersonationToken = token;
  session = {
    active: true,
    targetName: targetDisplayName(data?.target),
    targetUsername: data?.target?.userName || '',
    expiresInSeconds,
    expiresAt: Date.now() + expiresInSeconds * 1000,
    keycloakSwitched: data?.keycloak_switched === true,
    externalPlatformsSwitched: data?.external_platforms_switched === true,
  };
  writeTabSession();
  emit();
}

function finishLocalSession() {
  const wasActive = Boolean(impersonationToken);
  impersonationToken = null;
  session = inactiveSnapshot();
  clearTabSession();
  restoreSuperadminStorage();
  savedStorage = null;
  emit();
  return wasActive;
}

export function expireImpersonation() {
  return finishLocalSession();
}

export function suppressSuperadminReturnRedirect() {
  skipDashboardRedirect = true;
}

export function resumeSuperadminReturnRedirect() {
  skipDashboardRedirect = false;
}

export function consumeSuperadminReturnRedirect() {
  const skip = skipDashboardRedirect;
  skipDashboardRedirect = false;
  return skip;
}

export async function startImpersonation(userUuid, reason, client) {
  const superadminToken = rememberSuperadminToken();
  if (!superadminToken) {
    return {
      ok: false,
      message: 'The superadmin session is missing. Stay signed in as superadmin and try again.',
    };
  }

  const body = { user_uuid: userUuid };
  const cleanedReason = typeof reason === 'string' ? reason.trim() : '';
  if (cleanedReason) {
    body.reason = cleanedReason.slice(0, 500);
  }

  try {
    const response = await client.post('main/impersonation/start', body);
    const payload = response?.data;
    const data = payload?.data;
    if (!payload?.status || !data?.impersonation_token) {
      if (!impersonationToken) savedStorage = null;
      return { ok: false, message: payload?.message || 'Could not sign in as this user.' };
    }
    adoptSession(data.impersonation_token, data);
    return { ok: true, targetName: session.targetName };
  } catch (error) {
    if (!impersonationToken) savedStorage = null;
    return {
      ok: false,
      message: error?.response?.data?.message || 'Could not sign in as this user.',
    };
  }
}

export async function exitImpersonation(client) {
  if (!impersonationToken) {
    return { ok: true, alreadyEnded: true };
  }
  try {
    const response = await client.post('main/impersonation/exit', {});
    const payload = response?.data || {};
    if (!payload.status) {
      return {
        ok: false,
        message: payload.message || 'Could not return to the superadmin account.',
      };
    }
    finishLocalSession();
    return { ok: true, message: payload.message };
  } catch (error) {
    if (error?.response?.status === 401) {
      finishLocalSession();
      return { ok: true, expired: true };
    }
    return {
      ok: false,
      message: error?.response?.data?.message || 'Could not return to the superadmin account.',
    };
  }
}

export function resetImpersonationForTests() {
  impersonationToken = null;
  savedStorage = null;
  session = inactiveSnapshot();
  clearTabSession();
  listeners.clear();
  lastNotice = { message: '', at: 0 };
  skipDashboardRedirect = false;
}

export function reloadImpersonationForTests() {
  impersonationToken = null;
  savedStorage = null;
  session = inactiveSnapshot();
  restorePersistedSession();
}

export function adoptImpersonationForTests({
  token,
  targetName = 'Learner Example',
  targetUsername = 'learner@example.com',
  expiresInSeconds = DEFAULT_IMPERSONATION_SECONDS,
} = {}) {
  if (!savedStorage) {
    savedStorage = readStorage();
  }
  impersonationToken = token;
  session = {
    active: true,
    targetName,
    targetUsername,
    expiresInSeconds,
    expiresAt: Date.now() + expiresInSeconds * 1000,
    keycloakSwitched: false,
    externalPlatformsSwitched: false,
  };
  writeTabSession();
  emit();
}

restorePersistedSession();
